<template>
  <div class="config-imagem-item">
    <div class="row align-items-start">
      <!-- Lado Esquerdo: Informações -->
      <div class="col-md-5">
        <div class="imagem-info">
          <h6 class="mb-2">
            <i class="bi bi-image me-2 text-primary"></i>
            {{ config.nomeAmigavel }}
            <span v-if="config.obrigatorio" class="text-danger">*</span>
          </h6>

          <p class="text-muted small mb-2">{{ config.descricao }}</p>

          <div class="especificacoes mb-2">
            <span v-if="config.dimensaoRecomendada" class="badge bg-info-subtle text-info-emphasis me-1">
              <i class="bi bi-rulers me-1"></i>{{ config.dimensaoRecomendada }}
            </span>
            <span v-if="config.larguraMinima" class="badge bg-warning-subtle text-warning-emphasis me-1">
              <i class="bi bi-arrow-left-right me-1"></i>Larg. mín: {{ config.larguraMinima }}px
            </span>
            <span v-if="config.larguraMaxima" class="badge bg-warning-subtle text-warning-emphasis me-1">
              <i class="bi bi-arrow-left-right me-1"></i>Larg. máx: {{ config.larguraMaxima }}px
            </span>
            <span v-if="config.alturaMinima" class="badge bg-warning-subtle text-warning-emphasis me-1">
              <i class="bi bi-arrow-up-down me-1"></i>Alt. mín: {{ config.alturaMinima }}px
            </span>
            <span v-if="config.alturaMaxima" class="badge bg-warning-subtle text-warning-emphasis me-1">
              <i class="bi bi-arrow-up-down me-1"></i>Alt. máx: {{ config.alturaMaxima }}px
            </span>
            <span v-if="config.proporcaoLargura && config.proporcaoAltura" class="badge bg-warning-subtle text-warning-emphasis me-1">
              <i class="bi bi-aspect-ratio me-1"></i>Proporção {{ config.proporcaoLargura }}:{{ config.proporcaoAltura }}
            </span>
            <span v-if="config.formatosPermitidos" class="badge bg-secondary-subtle text-secondary-emphasis">
              <i class="bi bi-file-earmark me-1"></i>{{ config.formatosPermitidos }}
            </span>
          </div>

          <div v-if="imagemAtual" class="dados-imagem small text-muted">
            <span v-if="imagemAtual.larguraOriginal && imagemAtual.alturaOriginal">
              <i class="bi bi-aspect-ratio me-1"></i>
              {{ imagemAtual.larguraOriginal }}x{{ imagemAtual.alturaOriginal }}px
            </span>
            <span v-if="imagemAtual.tamanhoBytes" class="ms-2">
              <i class="bi bi-hdd me-1"></i>
              {{ formatarTamanho(imagemAtual.tamanhoBytes) }}
            </span>
          </div>

          <div v-if="config.instrucoesCliente" class="alert alert-light border mt-2 py-2 small mb-0">
            <i class="bi bi-lightbulb text-warning me-1"></i>
            {{ config.instrucoesCliente }}
          </div>
        </div>
      </div>

      <!-- Centro: Upload -->
      <div class="col-md-4">
        <div
          class="upload-area"
          :class="{
            'drag-over': isDragOver,
            'has-image': imagemAtual,
            'is-invalid': erroValidacao
          }"
          @dragover.prevent="isDragOver = true"
          @dragleave="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            :accept="acceptTypes"
            class="d-none"
            @change="handleFileSelect"
          />

          <template v-if="!imagemAtual && !isUploading">
            <i class="bi bi-cloud-arrow-up fs-2 text-muted mb-2"></i>
            <p class="text-muted small mb-1">Clique ou arraste uma imagem</p>
            <p class="text-muted small">Máx: 10MB</p>
          </template>

          <template v-if="isUploading">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="text-muted small mt-2">Enviando...</p>
          </template>

          <template v-if="imagemAtual && !isUploading">
            <img
              :src="imagemAtual.urlImagem"
              :alt="config.nomeAmigavel"
              class="preview-imagem"
            />
            <div class="overlay-actions">
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click.stop="$refs.fileInput.click()"
              >
                <i class="bi bi-pencil"></i> Alterar
              </button>
              <button
                type="button"
                class="btn btn-sm btn-danger ms-1"
                @click.stop="removerImagem"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </template>
        </div>

        <div v-if="erroValidacao" class="text-danger small mt-1">
          <i class="bi bi-exclamation-circle me-1"></i>{{ erroValidacao }}
        </div>
      </div>

      <!-- Lado Direito: Preview/Exemplo -->
      <div class="col-md-3">
        <div class="preview-contexto">
          <label class="small text-muted mb-2 d-block">Preview:</label>
          <div class="preview-box" :class="`preview-${config.tipoImagem}`">
            <img
              v-if="imagemAtual"
              :src="imagemAtual.urlImagem"
              :alt="config.nomeAmigavel"
            />
            <img
              v-else-if="config.exemploVisual"
              :src="config.exemploVisual"
              :alt="'Exemplo ' + config.nomeAmigavel"
              class="exemplo-imagem opacity-50"
            />
            <div v-else class="placeholder-exemplo">
              <i class="bi bi-image text-muted fs-1"></i>
            </div>
          </div>
          <p class="text-muted small mt-1 text-center mb-0">
            {{ imagemAtual ? 'Sua imagem' : 'Exemplo' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import api from '@/services/api'

const props = defineProps({
  config: {
    type: Object,
    required: true
  },
  idEmpresa: {
    type: Number,
    required: true
  },
  imagemAtual: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['imagem-atualizada', 'imagem-removida'])

const fileInput = ref(null)
const isDragOver = ref(false)
const isUploading = ref(false)
const erroValidacao = ref('')

const acceptTypes = computed(() => {
  const formatos = props.config.formatosPermitidos?.split(',') || ['png', 'jpg', 'jpeg']
  return formatos.map(f => `.${f.trim().toLowerCase()}`).join(',')
})

const formatarTamanho = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const handleDrop = (e) => {
  isDragOver.value = false
  const files = e.dataTransfer.files
  if (files.length > 0) {
    processarArquivo(files[0])
  }
}

const handleFileSelect = (e) => {
  const file = e.target.files[0]
  if (file) {
    processarArquivo(file)
  }
}

const processarArquivo = async (file) => {
  erroValidacao.value = ''
  isUploading.value = true

  try {
    const formData = new FormData()
    formData.append('Arquivo', file)
    formData.append('TipoImagem', props.config.tipoImagem)
    formData.append('IdEmpresa', props.idEmpresa.toString())

    const validacao = await api.post('/EmpresaImagens/validar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (!validacao.data.valido) {
      erroValidacao.value = validacao.data.erros.join('; ')
      isUploading.value = false
      return
    }

    const response = await api.post('/EmpresaImagens/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    emit('imagem-atualizada', response.data)
  } catch (error) {
    erroValidacao.value = error.response?.data?.message || 'Erro ao enviar imagem'
  } finally {
    isUploading.value = false
  }
}

const removerImagem = async () => {
  if (!props.imagemAtual) return

  if (!confirm('Deseja realmente remover esta imagem?')) return

  try {
    await api.delete(`/EmpresaImagens/${props.imagemAtual.idEmpresaImagem}`)
    emit('imagem-removida', props.config.tipoImagem)
  } catch (error) {
    erroValidacao.value = 'Erro ao remover imagem'
  }
}
</script>

<style scoped>
.config-imagem-item {
  padding: 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.config-imagem-item:last-child {
  border-bottom: none;
}

.upload-area {
  border: 2px dashed var(--bs-border-color);
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary-bg-subtle);
}

.upload-area.has-image {
  border-style: solid;
  border-color: var(--bs-success);
  padding: 0.5rem;
}

.upload-area.is-invalid {
  border-color: var(--bs-danger);
}

.preview-imagem {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}

.overlay-actions {
  margin-top: 0.5rem;
}

.preview-box {
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--bs-body-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-logo_principal,
.preview-logo_login {
  height: 80px;
}

.preview-logo_compacto {
  width: 64px;
  height: 64px;
  margin: 0 auto;
}

.preview-imagem_login {
  height: 120px;
}

.preview-banner_dashboard {
  height: 60px;
}

.preview-marca_dagua {
  height: 80px;
}

.preview-marca_dagua img {
  opacity: 0.3;
}

.preview-imagem_erro,
.preview-imagem_vazio {
  height: 80px;
}

.placeholder-exemplo {
  height: 80px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bs-secondary-bg);
}
</style>
